import {
  createContext,
  type ReactNode,
  type Ref,
  use,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
} from 'react'
import cx from 'classnames'
import { addDays, isSameDay } from 'date-fns'
import { Avatar } from '@/components/Avatar'
import { LayoutGroup, LazyMotion, m } from 'motion/react'
import {
  GridList as AriaGridList,
  GridListItem as AriaGridListItem,
} from 'react-aria-components'
import styles from './ConversationMessageList.module.css'
import { match } from '@/utils/pattern-match-util'
import flattenChildren from 'react-keyed-flatten-children'
import { Prose } from '@/components/Prose'
import { RawButton } from '@/components/Button'
import {
  AutoSkeleton,
  IsSkeletonContext,
  SkeletonOverlay,
  useIsSkeleton,
} from '@/components/Skeleton'
import type { PressEventHandler } from '@/infra/prop-types'
import { useSkeletonListContext } from '@/components/Skeleton/SkeletonList'
import { useComposedRefs } from '@radix-ui/react-compose-refs'
import { mergeProps } from 'react-aria'
import { IconButton } from '@/components/IconButton'

const loadMotionFeatures = () =>
  import('@/infra/motion-features').then((r) => r.default)

export interface ConversationMessageListProps<T extends object = object> {
  /**
   * The children to render inside the conversation message list.
   * Should be a list of {@link ConversationMessage},
   * {@link ConversationMessageListTimeItem}, and
   * {@link ConversationMessageListTimelineEvent} components.
   */
  children?: ReactNode | ((item: T) => ReactNode)
  /**
   * Items to render if using the `children`-as-a-function prop.
   */
  items?: T[]
  /**
   * The ARIA label for the conversation message list.
   */
  'aria-label'?: string
  /**
   * The ARIA labelled-by for the conversation message list.
   */
  'aria-labelledby'?: string
  /**
   * Element used to track whether a message is in view.
   */
  scrollingElement?: HTMLElement | null
  /**
   * Render-function for an empty state when there are no items in the message
   * list.
   */
  renderEmptyState?: () => ReactNode
  testId?: string
}

/**
 * Context for passing props to a {@link ConversationMessageList}.
 */
export const ConversationMessageListContext = createContext<
  Partial<ConversationMessageListProps>
>({})

/**
 * A list of messages in a conversation.
 */
export function ConversationMessageList<T extends object = object>(
  props: ConversationMessageListProps<T>,
) {
  const contextProps = use(ConversationMessageListContext)
  const {
    children,
    scrollingElement: scrollingElementProp,
    items,
    testId,
    ...rest
  } = mergeProps(contextProps, props)

  const groupId = useId()
  const internalContextValue = useConversationListState({
    scrollingElement: scrollingElementProp ?? document.documentElement,
  })

  return (
    <LazyMotion features={loadMotionFeatures}>
      <InternalConversationListContext.Provider value={internalContextValue}>
        <m.div className={cx(styles.root)} role="log" data-test={testId}>
          <LayoutGroup id={groupId}>
            <AriaGridList
              layout="stack"
              disallowTypeAhead
              className={styles.messages}
              items={items}
              aria-label="Messages"
              {...rest}
            >
              {typeof children === 'function'
                ? children
                : flattenChildren(children)}
            </AriaGridList>
          </LayoutGroup>
        </m.div>
      </InternalConversationListContext.Provider>
    </LazyMotion>
  )
}

interface ConversationMessageListTimelineEventProps {
  /**
   * Specify if using a function renderer for the list children.
   */
  id?: string
  /**
   * The label to display.
   */
  label: string
  /**
   * The text value of the timeline event, used for accessibility.
   */
  textValue?: string
  /**
   * Whether to show a skeleton for the timeline event.
   */
  isSkeleton?: boolean
  /**
   * A ref to the timeline event element.
   */
  ref?: Ref<HTMLDivElement>
  /**
   * Callback to be called when the timeline event enters the viewport.
   * IMPORTANT: Only specify this when actually interested in the event.
   * If not specified, then it won't cause any overhead with
   * the underlying intersection observer, so only pass in when needed.
   * Additionally, when passing in a callback, make sure it's memoized
   * so that it doesn't cause unnecessary re-registrations.
   */
  onEnterViewport?: () => unknown
}

/**
 * A marker point in the timeline of a conversation.
 */
export function ConversationMessageListTimelineEvent({
  id,
  label,
  textValue,
  isSkeleton,
  ref,
  onEnterViewport,
}: ConversationMessageListTimelineEventProps) {
  isSkeleton = useIsSkeleton(isSkeleton)

  const { observeElementVisibility } = useInternalConversationListContext()

  const connect = useCallback(
    (element: Element | null) =>
      observeElementVisibility(element, onEnterViewport),
    [onEnterViewport, observeElementVisibility],
  )

  const mergedRef = useComposedRefs(connect, ref)

  return (
    <AriaGridListItem
      id={id}
      ref={mergedRef}
      textValue={textValue || label}
      className={styles.timelineEvent}
    >
      <p className={styles.timelineEventLabel}>
        <AutoSkeleton color="secondary" isSkeleton={isSkeleton}>
          {label}
        </AutoSkeleton>
      </p>
    </AriaGridListItem>
  )
}

const timeGroupDateThisYearFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  weekday: 'long',
  month: 'long',
})

const timeGroupDateOtherYearFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  weekday: 'long',
  month: 'long',
  year: 'numeric',
})

interface ConversationMessageListTimeItemProps {
  /**
   * The date to display.
   */
  date: Date
  /**
   * The current date, used for relative formatting.
   */
  now: Date
  /**
   * Whether to show a skeleton for the time item.
   */
  isSkeleton?: boolean
  /**
   * A ref to the time item element.
   */
  ref?: Ref<HTMLDivElement>
}

/**
 * A header item for a group of messages by the day of which
 * the messages were sent
 */
export function ConversationMessageListTimeItem({
  now,
  date,
  isSkeleton,
  ref,
}: ConversationMessageListTimeItemProps) {
  const formattedDate = useMemo(() => {
    if (isSameDay(date, now)) {
      return 'Today'
    }

    const yesterday = addDays(now, -1)
    if (isSameDay(date, yesterday)) {
      return 'Yesterday'
    }

    if (date.getFullYear() === now.getFullYear()) {
      return timeGroupDateThisYearFormatter.format(date)
    }

    return timeGroupDateOtherYearFormatter.format(date)
  }, [now, date])

  return (
    <ConversationMessageListTimelineEvent
      id={`day-${date.getTime()}`}
      label={formattedDate}
      textValue={formattedDate}
      isSkeleton={isSkeleton}
      ref={ref}
    />
  )
}

const timeOnlyFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: 'numeric',
  hour12: true,
})

export interface ConversationMessageProps {
  /**
   * Specify if using a function renderer for the list children.
   */
  id?: string
  /**
   * The text value of the message, used for accessibility.
   */
  textValue?: string
  /**
   * The content of the message.
   */
  children?: ReactNode
  /**
   * The display name of the message sender.
   */
  displayName: string
  /**
   * The URL of the sender's avatar.
   */
  avatarUrl?: string | null | undefined
  /**
   * If specified, indicates the role of the user (e.g. "Admin") and will be
   * displayed by their message.
   */
  roleLabel?: string | null | undefined
  /**
   * The date and time the message was sent.
   */
  time: Date
  /**
   * The variant of the message.
   */
  variant: 'incoming' | 'outgoing'
  /**
   * A unique ID to group messages by. This is used for
   * the avatar layout animation.
   */
  avatarLayoutGroup: string | number
  /**
   * Whether the message is the first in a group.
   */
  isFirstInGroup?: boolean
  /**
   * Whether the message is the last in a group.
   */
  isLastInGroup?: boolean
  /**
   * Whether to show a skeleton for the message.
   */
  isSkeleton?: boolean
  /**
   * A ref to the message element.
   */
  ref?: Ref<HTMLDivElement>
  /**
   * Callback to be called when the message enters the viewport.
   * IMPORTANT: Only specify this when actually interested in the event.
   * If not specified, then it won't cause any overhead with
   * the underlying intersection observer, so only pass in when needed.
   * Additionally, when passing in a callback, make sure it's memoized
   * so that it doesn't cause unnecessary re-registrations.
   */
  onEnterViewport?: () => unknown
  /**
   * When specified, shows a retry button next to the message in the gutter.
   * This should only be used for outgoing messages.
   */
  onRetryPress?: () => unknown
  /**
   * For outgoing messages only, determines whether the message is currently in
   * the process of being sent, or if sending failed. Omit for all other cases.
   */
  sendState?: 'sending' | 'failed'
  /**
   * Test ID for the message element.
   */
  testId?: string
}

/**
 * A message in a conversation.
 * For rich content, nest a {@link Prose} element.
 */
export function ConversationMessage({
  id,
  displayName,
  avatarUrl,
  roleLabel,
  time,
  children,
  textValue,
  variant,
  avatarLayoutGroup = 'unset',
  sendState,
  isFirstInGroup,
  isLastInGroup,
  isSkeleton,
  ref,
  onEnterViewport,
  onRetryPress,
  testId,
}: ConversationMessageProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  const contextValue = useMemo(() => ({ variant }), [variant])
  const { observeElementVisibility } = useInternalConversationListContext()

  const connect = useCallback(
    (element: Element | null) =>
      observeElementVisibility(element, onEnterViewport),
    [onEnterViewport, observeElementVisibility],
  )

  const mergedRef = useComposedRefs(connect, ref)

  const bullet = <span className={styles.separator}>&bull;</span>

  const screenReaderDisplayName = roleLabel
    ? `${displayName} (${roleLabel})`
    : displayName

  return (
    <AriaGridListItem
      id={id}
      ref={mergedRef}
      textValue={textValue}
      isDisabled={isSkeleton}
      data-test={testId}
      className={cx(
        match(variant, {
          incoming: styles.incoming,
          outgoing: styles.outgoing,
        }),
        isFirstInGroup && styles.firstInGroup,
        isLastInGroup && styles.lastInGroup,
        isSkeleton && styles.skeleton,
        sendState === 'sending' && styles.sending,
        sendState === 'failed' && styles.failed,
      )}
    >
      <InternalConversationMessageContext value={contextValue}>
        <m.div layout layoutRoot className={styles.gutter}>
          {isLastInGroup && variant === 'incoming' && (
            <m.div
              aria-hidden
              layout="position"
              layoutId={`avatar-${avatarLayoutGroup}`}
              transition={{
                layout: {
                  type: 'spring',
                  stiffness: 100,
                  damping: 15,
                },
              }}
            >
              <Avatar
                isSkeleton={isSkeleton}
                skeletonColor="secondary"
                size={28}
                subject={{ avatarUrl, displayName }}
              />
            </m.div>
          )}
          {onRetryPress && (
            <IconButton
              name="warning"
              label="Sending failed. Press to try resending"
              onPress={onRetryPress}
              variant="danger"
              tooltipPlacement="left"
            />
          )}
        </m.div>
        <div className={styles.messageMain}>
          {/* For screen readers, add a message sender name to the first message in a group */}
          {isFirstInGroup && (
            <span className="sr-only">{screenReaderDisplayName} sent </span>
          )}
          <div data-private="" className={styles.bubble}>
            {isSkeleton && (
              <SkeletonOverlay
                color="secondary"
                borderRadius="var(--border-radius)"
              />
            )}
            {/* Since the entire element is being overlaid, we don't need to set skeletons on children */}
            <IsSkeletonContext value={false}>{children}</IsSkeletonContext>
          </div>

          {isLastInGroup && (
            <div className={styles.senderInfo}>
              <AutoSkeleton color="secondary" isSkeleton={isSkeleton}>
                {displayName}
                {roleLabel ? (
                  <>
                    {' '}
                    {bullet} {roleLabel}
                  </>
                ) : null}{' '}
                {bullet} {timeOnlyFormatter.format(time)}
              </AutoSkeleton>
            </div>
          )}
        </div>
      </InternalConversationMessageContext>
    </AriaGridListItem>
  )
}

export interface ConversationMessageContextValue {
  variant: ConversationMessageProps['variant']
}

/**
 * Context used to pass properties to content components so they know
 * whether they represent an incoming or outgoing message.
 */
const InternalConversationMessageContext =
  createContext<ConversationMessageContextValue>({
    variant: 'incoming',
  })

interface ConversationMessageTextContentProps {
  children?: ReactNode
  testId?: string
}

/**
 * Content to render inside a {@link ConversationMessage} for formatted content.
 */
export function ConversationMessageTextContent({
  children,
  testId,
}: ConversationMessageTextContentProps) {
  const { variant } = use(InternalConversationMessageContext)
  return (
    <div className={styles.textContent} data-test={testId}>
      <Prose variant={variant === 'outgoing' ? 'invert' : 'default'}>
        {children}
      </Prose>
    </div>
  )
}

interface ConversationMessageCardContentProps {
  children?: ReactNode
  title: ReactNode
  description?: ReactNode
}

/**
 * Content to render inside a {@link ConversationMessage} for card-like content.
 */
export function ConversationMessageCardContent({
  title,
  description,
  children,
}: ConversationMessageCardContentProps) {
  const flattenedChildren = flattenChildren(children)
  return (
    <>
      <div className={styles.cardContent}>
        <p className={styles.cardTitle}>{title}</p>
        {description && <p className={styles.cardDescription}>{description}</p>}
      </div>
      {flattenedChildren.length > 0 && (
        <div className={styles.cardFooter}>{children}</div>
      )}
    </>
  )
}

interface ConversationMessageCardContentButtonProps {
  isDisabled?: boolean
  isPending?: boolean
  isSkeleton?: boolean
  label: string
  onPress?: PressEventHandler
}

/**
 * Button to render inside a {@link ConversationMessageCardContent}.
 */
export function ConversationMessageCardContentButton({
  isSkeleton,
  isDisabled,
  isPending,
  label,
  onPress,
}: ConversationMessageCardContentButtonProps) {
  isSkeleton = useIsSkeleton(isSkeleton)
  return (
    <RawButton
      isDisabled={isDisabled}
      isPending={isPending}
      onPress={onPress}
      className={({
        isHovered,
        isPending,
        isDisabled,
        isPressed,
        isFocusVisible,
      }) =>
        cx(
          styles.cardButton,
          isSkeleton && styles.skeleton,
          isPending && styles.pending,
          isPressed && styles.pressed,
          isHovered && styles.hovered,
          isDisabled && styles.disabled,
          isFocusVisible && styles.focusVisible,
        )
      }
    >
      <AutoSkeleton isSkeleton={isSkeleton}>{label}</AutoSkeleton>
    </RawButton>
  )
}

interface ConversationMessageCardContentStatusProps {
  children?: ReactNode
}

/**
 * Used to a status pertaining to the card's content.
 */
export function ConversationMessageCardContentStatus({
  children,
}: ConversationMessageCardContentStatusProps) {
  return <div className={styles.cardContentStatus}>{children}</div>
}

const SKELETON_MULTIPLIERS = [1.5, 0.8, 1.4, 0.6, 0.4, 2, 0.2, 1.2, 0.4, 1]

/**
 * Conversation Message List Item Skeleton
 */
export function ConversationMessageListItemSkeleton() {
  const { ref, index, vary } = useSkeletonListContext()
  const date = new Date(0)

  const varyString = (count: number) =>
    'a '.repeat(Math.round(vary(count, SKELETON_MULTIPLIERS)))

  const messageProps: ConversationMessageProps = {
    ref: ref as Ref<HTMLDivElement>,
    isSkeleton: true,
    variant: 'incoming',
    time: date,
    avatarLayoutGroup: `skeleton-${index}`,
    displayName: 'Loading',
    textValue: 'Loading',
    children: (
      <ConversationMessageTextContent>
        <span aria-hidden>{varyString(20)}</span>
      </ConversationMessageTextContent>
    ),
  }

  const modulo = index % 10
  switch (modulo) {
    case 0:
      return (
        <ConversationMessageListTimelineEvent
          ref={ref as Ref<HTMLDivElement>}
          isSkeleton
          label="Loading messages, please wait"
          textValue="Loading"
        />
      )
    case 1:
    case 2:
    case 3:
      return (
        <ConversationMessage
          {...messageProps}
          isFirstInGroup={modulo === 1}
          isLastInGroup={modulo === 3}
          variant="incoming"
        />
      )
    case 4:
    case 5:
      return (
        <ConversationMessage
          {...messageProps}
          isFirstInGroup={modulo === 4}
          isLastInGroup={modulo === 5}
          variant="outgoing"
          displayName="You"
        />
      )
    case 6:
    case 8:
    case 10:
      return (
        <ConversationMessage
          {...messageProps}
          isFirstInGroup
          isLastInGroup
          variant="incoming"
        />
      )
    case 7:
    case 9:
      return (
        <ConversationMessage
          {...messageProps}
          isFirstInGroup
          isLastInGroup
          variant="outgoing"
          displayName="You"
        />
      )
  }
}

interface InternalConversationListContextValue {
  observer: IntersectionObserver | null
  registrations: Map<Element, () => unknown>
  observeElementVisibility: (
    element: Element | null,
    callback: (() => unknown) | null | undefined,
  ) => (() => void) | undefined
}

/**
 * Context for the {@link ConversationMessageList} component.
 */
const InternalConversationListContext =
  createContext<InternalConversationListContextValue | null>(null)

/**
 * Custom hook to access the internal conversation list context.
 * This hook retrieves the context value from `InternalConversationListContext`.
 * It must be used within a `ConversationMessageList` component.
 */
function useInternalConversationListContext() {
  const context = use(InternalConversationListContext)
  if (!context) {
    throw new Error(
      'useInternalConversationListContext must be used within a ConversationMessageList',
    )
  }

  return context
}

interface UseConversationListStateProps {
  /**
   * Represents the element that serves as the scrollable container for the
   * conversation list.
   */
  scrollingElement: Element
}

/**
 * Hook to manage and maintain the state for a conversation list, specifically
 * related to observing when messages enter the viewport.
 */
function useConversationListState({
  scrollingElement,
}: UseConversationListStateProps) {
  const stateRef = useRef<InternalConversationListContextValue | null>(null)

  if (!stateRef.current) {
    const state: InternalConversationListContextValue = {
      observer: null,
      registrations: new Map(),
      observeElementVisibility(element, callback) {
        if (!element || !callback) {
          return undefined
        }

        state.registrations.set(element, callback)
        state.observer?.observe(element)
        return () => {
          state.registrations.delete(element)
          state.observer?.unobserve(element)
        }
      },
    }

    stateRef.current = state
  }

  useEffect(() => {
    if (!stateRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            stateRef.current?.registrations.get(entry.target)?.()
          }
        })
      },
      {
        // If specifying the document element, pass `null` so tracking is done
        // on the window.
        root:
          scrollingElement === document.documentElement
            ? null
            : scrollingElement,
        rootMargin: '0px',
        threshold: 0.7,
      },
    )

    // We got a new observer, so observe all our collected elements.
    stateRef.current.observer = observer
    stateRef.current.registrations.forEach((_, element) => {
      observer.observe(element)
    })
    return () => {
      observer.disconnect()
      if (stateRef.current) {
        stateRef.current.observer = null
      }
    }
  }, [scrollingElement])

  return stateRef.current
}

interface ConversationMessageListEmptyStateProps {
  title?: ReactNode
  description?: ReactNode
}

/**
 * Conversation Message List Empty State
 */
export function ConversationMessageListEmptyState({
  title,
  description,
}: ConversationMessageListEmptyStateProps) {
  return (
    <div className={styles.emptyState}>
      <p className={styles.emptyStateTitle}>{title}</p>
      <p className={styles.emptyStateDescription}>{description}</p>
    </div>
  )
}
